import type { OrderStatus, Order, OrderItemOption } from './orders-client';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

/**
 * A rule refused the edit. The backend answers with a machine code plus whatever context the panel
 * needs to explain it (`DELIVERY_OUT_OF_ZONE`, `MISSING_REQUIRED_OPTIONS`, `DRAFT_MODIFIED`, …), and
 * a plain `Error` would throw all of that away — the operator would get "422" and no way forward.
 */
export class DraftOrderError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly data: Record<string, unknown> = {},
  ) {
    super(code);
    this.name = 'DraftOrderError';
  }
}

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
}

async function api<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body } = options;

  const fetchOptions: RequestInit = {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  };

  if (body && method !== 'GET') {
    fetchOptions.body = JSON.stringify(body);
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, fetchOptions);

  if (response.status === 401 || response.status === 403) {
    window.dispatchEvent(new CustomEvent('auth:unauthorized', { detail: { status: response.status } }));
    throw new Error(`Unauthorized (${response.status})`);
  }

  if (!response.ok) {
    throw await toDraftOrderError(response);
  }

  return response.json();
}

/**
 * Nest returns an object payload from `new HttpException({ code, ... })` verbatim as the body, so
 * the machine code sits at the top level rather than under `message`.
 */
async function toDraftOrderError(response: Response): Promise<Error> {
  try {
    const body = (await response.json()) as Record<string, unknown>;
    const code = typeof body.code === 'string' ? body.code : `HTTP_${response.status}`;
    return new DraftOrderError(response.status, code, body);
  } catch {
    return new DraftOrderError(response.status, `HTTP_${response.status}`);
  }
}

// ─── Types ─────────────────────────────────────────────────────

/** Everything standing between this cart and a real order. Rendered as the Confirm button's reasons. */
export type DraftBlockerCode =
  | 'EMPTY_CART'
  | 'BUSINESS_CLOSED'
  | 'LOCATION_CLOSED'
  | 'DELIVERY_NOT_AVAILABLE'
  /** Delivery is offered here, but its own timetable is not running right now — pickup may still be. */
  | 'DELIVERY_CLOSED'
  | 'ADDRESS_NOT_CONFIRMED'
  | 'COORDINATES_REQUIRED'
  | 'DELIVERY_FEE_NOT_RESOLVED'
  /** The cart moved the fee after it was quoted (cart-priced stores); the new fee is already saved. */
  | 'DELIVERY_FEE_CHANGED'
  | 'TRANSFER_SCREENSHOT_MISSING'
  | 'BELOW_MINIMUM'
  /** Neither pickup nor delivery was ever set; nothing downstream supplies a default. */
  | 'FULFILLMENT_TYPE_REQUIRED'
  | 'PAYMENT_METHOD_REQUIRED'
  /** The applied coupon needs a bigger order than the cart holds. The coupon stays applied. */
  | 'COUPON_MINIMUM_NOT_MET'
  /** The coupon expired, was disabled, or its window closed while the cart sat open. */
  | 'COUPON_INVALID'
  /** The coupon is valid but its weekly schedule is not running right now — it comes back later. */
  | 'COUPON_SCHEDULE_NOT_ACTIVE'
  /** The coupon's global or per-customer usage cap was reached before this cart confirmed. */
  | 'COUPON_LIMIT_REACHED';

export interface DraftBlocker {
  code: DraftBlockerCode;
  data?: Record<string, unknown>;
}

export type PaymentPreference =
  'cash' | 'send_pos_for_card' | 'bank_transfer' | 'mercado_pago' | 'card' | 'pay_at_store';

export interface CartViewItem {
  itemIndex: number;
  catalogItemId: string;
  name: string;
  description?: string;
  quantity: number;
  /** The price the line was added at — what `subtotal` and the order's `total` are built from. */
  unitPrice: number;
  /**
   * What the menu charges now, present only when it differs from `unitPrice` — i.e. the item was
   * repriced under this cart. Saving re-prices the line; until then the order stands at `unitPrice`.
   */
  catalogUnitPrice?: number;
  options?: OrderItemOption[];
  comments?: string;
  subtotal: number;
  /** False when the catalog now marks it sold out; undefined when the item is gone entirely. */
  available?: boolean;
  /** The item no longer exists in the active catalog — it can only be removed, not re-priced. */
  orphaned: boolean;
}

export interface CartDeliveryAddress {
  raw: string;
  confirmed?: boolean;
  instructions?: string;
  coordinates?: { lat: number; lng: number };
  deliveryZoneName?: string;
  deliveryFeePending?: boolean;
}

export interface CartPaymentContext {
  availablePaymentMethods: PaymentPreference[];
  storedCard?: { label: string; instrumentId: string; selected: boolean };
  bankTransferDetails?: unknown;
  /**
   * The transfer payment is SETTLED — an active proof is on the order, receipt or manual confirmation
   * alike. This is the flag the agent, the web checkout and the flow key "stop asking for the
   * screenshot" on. Whether a file actually exists is `transferReceiptFile`.
   */
  transferReceiptAttached: boolean;
  /** A receipt file is on the active transfer row — the one thing "comprobante adjunto" may claim. */
  transferReceiptFile: boolean;
  /**
   * Where the order's transfer proof stands, absent when there is none. What `transferReceiptAttached`
   * alone cannot say: whether anything still needs a human to look at it.
   */
  transferStatus?: 'pending' | 'verified' | 'mismatch';
  /**
   * An operator vouched for the transfer themselves: the customer paid in a way the agent never saw,
   * or the receipt on file could not be matched. Present on every read afterwards, so the panel says
   * "confirmada manualmente" instead of claiming a comprobante was checked.
   */
  transferManualVerification?: {
    /** ISO datetime of the override. */
    at: string;
    /** False when nothing was ever attached — the operator's word is the only evidence. */
    hadReceipt: boolean;
    /** The status the receipt reader had left, when there was a receipt at all. */
    previousStatus?: string;
    reason?: string;
  };
}

export interface CartView {
  orderId: string;
  status: OrderStatus;
  brandId: string;
  conversationId?: string;
  customerId: string;
  customerPhone?: string;
  locationId?: string;
  locationName?: string;
  currency: string;
  currencySymbol: string;
  items: CartViewItem[];
  itemsTotal: number;
  deliveryFee?: number;
  tipAmount: number;
  /** Coupon discount already subtracted from `total`. 0 when no coupon is applied. */
  discountAmount: number;
  /**
   * The coupon on this cart, present even when `discountAmount` is 0 — an applied coupon whose
   * reward item is not in the cart yet is exactly the state an operator needs to be able to see.
   *
   * `code` is null for an automatic promotion, which is named by `name` instead. `auto` marks a
   * discount nobody typed (the brand's automatic promotion, or the POS's own) — removal is only
   * offered for a typed code, mirroring what the agent offers the customer.
   */
  appliedDiscount?: {
    code?: string | null;
    name?: string | null;
    auto?: boolean;
    discountId: string;
    minimumOrderAmount?: number | null;
  };
  total: number;
  deliveryType?: 'delivery' | 'pickup';
  deliveryAddress?: CartDeliveryAddress;
  deliveryAddressConfirmed: boolean;
  deliveryFeePending: boolean;
  notes?: string;
  paymentPreference?: PaymentPreference;
  payment?: CartPaymentContext;
  cartSummary: string;
  blockers?: DraftBlocker[];
  /** Last time an operator hand-edited this cart. Display only. */
  manuallyEditedAt?: string;
  /**
   * An operator changed something the customer agreed to and has not asked them about it yet.
   * Advice — it decides which exit the footer offers first, never whether confirming is allowed.
   */
  pendingCustomerApproval?: boolean;
  approvalReasons?: ApprovalReason[];
  updatedAt: string;
}

export type ApprovalReason =
  'TOTAL_CHANGED' | 'PAYMENT_METHOD_CHANGED' | 'DELIVERY_TYPE_CHANGED' | 'STORED_CARD_WILL_BE_CHARGED';

export interface CatalogOption {
  id: string;
  name: string;
  priceDelta?: number;
}

export interface CatalogOptionGroup {
  id: string;
  name: string;
  required: boolean;
  minChoices?: number;
  maxChoices?: number;
  options: CatalogOption[];
}

export interface CatalogItem {
  id: string;
  name: string;
  description?: string;
  price: number;
  promoPrice?: number;
  currency: string;
  available?: boolean;
  optionGroups?: CatalogOptionGroup[];
}

export interface CatalogCategory {
  id: string;
  name: string;
  description?: string;
  position?: number;
  items: CatalogItem[];
}

export interface DraftCatalog {
  categories: CatalogCategory[];
}

export interface SavedAddress {
  id: string;
  label?: string;
  raw: string;
  hasCoordinates: boolean;
}

/** The desired end state of the cart, not a sequence of edits. */
export interface SaveDraftPayload {
  items?: {
    catalogItemId: string;
    quantity?: number;
    options?: { groupId: string; optionId: string }[];
    comments?: string;
  }[];
  delivery?: {
    deliveryType?: 'delivery' | 'pickup';
    locationId?: string;
    address?: string;
    savedAddressId?: string;
    instructions?: string;
    deliveryFeeOverride?: number;
  };
  payment?: { paymentPreference?: PaymentPreference; useSavedCard?: boolean };
  notes?: string;
  tipAmount?: number;
  expectedUpdatedAt?: string;
}

/** An order this conversation placed minutes ago that nobody has dealt with yet. */
export interface RecentlyPlacedOrder {
  orderId: string;
  status: OrderStatus;
  placedAt: string;
}

/**
 * What the panel needs to offer starting a cart. Sent only when the conversation has none — with a
 * live cart there is nothing to start.
 */
export interface DraftStartContext {
  /** The branch a new order would be placed for, when the server could settle it without asking. */
  suggestedLocationId?: string;
  /** The session may only ever place for `suggestedLocationId` — there is no branch to choose. */
  locationLocked: boolean;
  recentOrder?: RecentlyPlacedOrder;
}

export interface ConversationDraft {
  cart: CartView | null;
  startContext?: DraftStartContext;
}

/** Which sections the customer's history filled in, and which it could not. */
export type StartDraftPrefillSection = 'delivery' | 'address' | 'payment';
export type StartDraftPrefillSkip =
  'NO_SAVED_ADDRESS' | 'ADDRESS_OUT_OF_ZONE' | 'ADDRESS_NOT_REUSABLE' | 'DELIVERY_NOT_AVAILABLE';

export interface StartDraftResult {
  cart: CartView;
  /** A cart already existed — this opened that one rather than creating a second. */
  alreadyExisted: boolean;
  prefill: { applied: StartDraftPrefillSection[]; skipped: StartDraftPrefillSkip[] };
}

/** A reward line the coupon put in the cart on its own (or found already there and discounted). */
export interface GrantedCouponItem {
  catalogItemId: string;
  name: string;
  quantity: number;
  /** True when applying the coupon ADDED the line rather than discounting one already there. */
  autoAdded: boolean;
}

/**
 * A reward the coupon could not place by itself — the item is sold out, gone from the catalog, or
 * has required option groups someone has to choose from. The operator adds it by hand.
 */
export interface PendingCouponReward {
  catalogItemId: string;
  name?: string;
  reason: 'UNAVAILABLE' | 'NEEDS_OPTIONS' | 'NOT_IN_CATALOG';
}

export interface ApplyCouponOutcome {
  cart: CartView;
  granted?: GrantedCouponItem[];
  pendingRewards?: PendingCouponReward[];
}

export interface ConfirmDraftResult {
  order: Order;
  cart: CartView;
  trackingUrl?: string;
  paymentLinkUrl?: string;
  paymentDeadlineAt?: string;
}

interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
}

// ─── Client ────────────────────────────────────────────────────

export const draftOrderClient = {
  async getForConversation(
    conversationId: string,
    options?: {
      /**
       * Also ask what starting a cart would take (suggested branch, recent-order warning) when
       * there is none. Costs the server extra lookups — only the open start panel should send it.
       */
      includeStartContext?: boolean;
    },
  ): Promise<ConversationDraft> {
    const query = options?.includeStartContext ? '?startContext=true' : '';
    const response = await api<Envelope<CartView | null> & { startContext?: DraftStartContext }>(
      `/orders/conversation/${conversationId}/draft${query}`,
    );
    return { cart: response.data, startContext: response.startContext };
  },

  /**
   * Open a cart on a conversation that has none. The server prefills it from the customer's last
   * order, so this usually lands on a cart that only needs items added.
   */
  async start(conversationId: string, options?: { locationId?: string }): Promise<StartDraftResult> {
    const response = await api<Envelope<CartView> & Omit<StartDraftResult, 'cart'>>(
      `/orders/conversation/${conversationId}/draft`,
      { method: 'POST', body: { locationId: options?.locationId } },
    );
    return { cart: response.data, alreadyExisted: response.alreadyExisted, prefill: response.prefill };
  },

  async getCatalog(orderId: string): Promise<DraftCatalog> {
    const response = await api<Envelope<DraftCatalog>>(`/orders/${orderId}/draft/catalog`);
    return response.data;
  },

  async getSavedAddresses(orderId: string): Promise<SavedAddress[]> {
    const response = await api<Envelope<SavedAddress[]>>(`/orders/${orderId}/draft/saved-addresses`);
    return response.data;
  },

  async save(orderId: string, payload: SaveDraftPayload): Promise<CartView> {
    const response = await api<Envelope<CartView>>(`/orders/${orderId}/draft`, { method: 'PUT', body: payload });
    return response.data;
  },

  /**
   * Put a coupon code on the draft. Refusals answer 422 with a `COUPON_*` code
   * (`COUPON_NOT_FOUND`, `COUPON_EXPIRED`, …) for `cart.errors` to name.
   */
  async applyCoupon(orderId: string, code: string): Promise<ApplyCouponOutcome> {
    const response = await api<Envelope<CartView> & Omit<ApplyCouponOutcome, 'cart'>>(
      `/orders/${orderId}/draft/coupon`,
      { method: 'POST', body: { code } },
    );
    return { cart: response.data, granted: response.granted, pendingRewards: response.pendingRewards };
  },

  /** Take the coupon off the draft. Reward lines it auto-added stay, back at full price. */
  async removeCoupon(orderId: string): Promise<CartView> {
    const response = await api<Envelope<CartView>>(`/orders/${orderId}/draft/coupon`, { method: 'DELETE' });
    return response.data;
  },

  /**
   * Confirm the cart's bank transfer by hand — the transfer a human verified outside this system.
   *
   * `TRANSFER_SCREENSHOT_MISSING` exists because the agent cannot know whether money moved; an
   * operator can. The order then carries a `verified` transfer like any other, marked as an override
   * everywhere it is shown afterwards. Refusals: 422 `PAYMENT_METHOD_NOT_TRANSFER`, 409 on a cart
   * that is no longer a draft.
   */
  async markTransferVerified(orderId: string, reason?: string): Promise<CartView> {
    const response = await api<Envelope<CartView>>(`/orders/${orderId}/draft/transfer-verification`, {
      method: 'POST',
      body: { reason },
    });
    return response.data;
  },

  /** Undo the manual confirmation — the cart goes back to needing a real comprobante. */
  async clearTransferVerification(orderId: string): Promise<CartView> {
    const response = await api<Envelope<CartView>>(`/orders/${orderId}/draft/transfer-verification`, {
      method: 'DELETE',
    });
    return response.data;
  },

  /**
   * `withoutCustomerApproval` is recorded on the timeline, not enforced — it marks a confirmation
   * the operator made over the panel's advice to ask the customer first.
   */
  async confirm(orderId: string, options?: { withoutCustomerApproval?: boolean }): Promise<ConfirmDraftResult> {
    const response = await api<Envelope<ConfirmDraftResult>>(`/orders/${orderId}/draft/confirm`, {
      method: 'POST',
      body: { withoutCustomerApproval: options?.withoutCustomerApproval ?? false },
    });
    return response.data;
  },
};
